type ChargeError = {
  status?: number | string;
  data?: { error?: string; message?: string; detail?: string };
  error?: string;
  message?: string;
};

type ChargeErrorSignature = {
  anyOf: string[][];
  description: string;
};

const BUSINESS_RULE_STATUS = 409;
const NOT_FOUND_STATUS = 404;

const COMPLETE_VESSEL_REGISTRATION =
  'Por favor, complete o cadastro do navio antes de tentar novamente.';

const SPECIFIC_SIGNATURES: ChargeErrorSignature[] = [
  {
    anyOf: [
      ['get_items_for_table_one', 'nonetype'],
      ['get_items_for_table_one', '<= not supported'],
      ['dwt', 'nonetype'],
      ['dwt', '<= not supported'],
      ['dead weight', 'required'],
    ],
    description: `Não foi possível enviar para faturamento pois o navio não possui o valor de DWT (Dead Weight Tonnage) cadastrado. ${COMPLETE_VESSEL_REGISTRATION}`,
  },
  {
    anyOf: [
      ['get_items_for_table_two', 'nonetype'],
      ['get_items_for_table_two', '<= not supported'],
      ['loa', 'nonetype'],
      ['loa', '<= not supported'],
      ['length overall', 'required'],
    ],
    description: `Não foi possível enviar para faturamento pois o navio não possui o valor de LOA (Length Overall/Comprimento Total) cadastrado. ${COMPLETE_VESSEL_REGISTRATION}`,
  },
  {
    anyOf: [
      ['get_items_for_table', 'grt', 'nonetype'],
      ['get_items_for_table', 'grt', '<= not supported'],
      ['grt', 'nonetype'],
      ['grt', '<= not supported'],
      ['gross tonnage', 'required'],
    ],
    description: `Não foi possível enviar para faturamento pois o navio não possui o valor de GT (Gross Tonnage/Arqueação Bruta) cadastrado. ${COMPLETE_VESSEL_REGISTRATION}`,
  },
  {
    anyOf: [
      ['nature_of_cargo'],
      ['get_items_for_table_one', 'natureza', 'carga'],
    ],
    description: `Não foi possível enviar para faturamento devido à ausência do campo "Natureza da Carga" no cadastro do navio. ${COMPLETE_VESSEL_REGISTRATION}`,
  },
  {
    anyOf: [
      ['financial_tables'],
      ['tabela financeira'],
      ['tabelas financeiras'],
      ['validate_charge', 'responsible'],
      ['responsible', 'company'],
    ],
    description:
      'Não foi possível enviar para faturamento porque não há tabelas financeiras ou empresa responsável definida para esta atracação. Contate o administrador do sistema.',
  },
];

const BROAD_SIGNATURE: ChargeErrorSignature = {
  anyOf: [['nonetype'], ['vessel'], ['navio']],
  description:
    'Não foi possível enviar para faturamento devido à ausência de dados obrigatórios no cadastro do navio. Verifique se todos os campos necessários estão preenchidos e tente novamente.',
};

const COMMUNICATION_SIGNATURE: ChargeErrorSignature = {
  anyOf: [['<!doctype'], ['unexpected token']],
  description:
    'Ocorreu um erro de comunicação com o servidor. Por favor, tente novamente mais tarde.',
};

function matches(serializedError: string, signature: ChargeErrorSignature) {
  return signature.anyOf.some((terms) =>
    terms.every((term) => serializedError.includes(term))
  );
}

export function getChargeErrorDescription(error: unknown): string {
  const chargeError = (error || {}) as ChargeError;
  const { status, data } = chargeError;
  const serializedError = JSON.stringify(chargeError).toLowerCase();

  // O backend responde 409 com a mensagem de negócio já traduzida. Ela é mais precisa
  // que qualquer assinatura daqui, então vem primeiro; as assinaturas ficam como
  // fallback para 500 e para respostas sem corpo estruturado, que é o caso em que a
  // heurística sobre o traceback vazado realmente serve.
  if (status === BUSINESS_RULE_STATUS && data?.message) {
    return data.message;
  }

  const specific = SPECIFIC_SIGNATURES.find((signature) =>
    matches(serializedError, signature)
  );
  if (specific) {
    return specific.description;
  }

  if (status === NOT_FOUND_STATUS) {
    return 'Atracação não encontrada ou você não tem permissão para executar esta ação.';
  }

  if (matches(serializedError, BROAD_SIGNATURE)) {
    return BROAD_SIGNATURE.description;
  }

  if (matches(serializedError, COMMUNICATION_SIGNATURE)) {
    return COMMUNICATION_SIGNATURE.description;
  }

  return (
    data?.detail ||
    data?.message ||
    chargeError.error ||
    chargeError.message ||
    'Ocorreu uma falha inesperada ao gerar o faturamento. Contate o administrador do sistema.'
  );
}
